import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { of, Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { environment, tokenSetter } from '../../../environments/environment';
import { IMfaResponse } from '../models/interfaces/ILoginResponse';
import { IRequestAccess } from '../models/interfaces/IRequestAccess';
import { IResponse } from '../models/interfaces/IResponse';
import { IUser } from '../models/interfaces/IUser';
import { IUserSettings } from '../models/interfaces/IUserSettings';
import { MixpanelHelper } from '../../shared/helpers/mixpanel.helper';
import { UserPermissions } from '../../shared/helpers/userPermissions.helper';

const CHANGE_PWD_KEY = 'need-change-pwd';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private url = environment.apiUrl + 'users';
  private adminUrl = environment.apiUrl + 'admin/users';
  private currenUser?: IUser | null;
  private passwordChangedSubject = new Subject();
  private loginChangedSubject = new Subject();

  constructor(
    private http: HttpClient,
    private jwtHelper: JwtHelperService,
    private mixpanelHelper: MixpanelHelper,
    private userPermissionService: UserPermissions
  ) {}

  async login(login: string, password: string): Promise<{ status: number }> {
    const response = await this.http
      .post<IResponse<any>>(
        `${this.url}/login`,
        { login, password },
        { observe: 'response' }
      )
      .toPromise(); // <-- RxJS 6

    return {
      status: response!.status,
    };
  }

  async verifyMfa(login: string, code: number) {
    const { data } = await this.http
      .post<IResponse<IMfaResponse>>(`${this.url}/login-mfa`, {
        login: login,
        code: code,
      })
      .toPromise();

    tokenSetter(data.token);
    data.changePassword && localStorage.setItem(CHANGE_PWD_KEY, 'true');

    this.userPermissionService.setPermissoes(data);

    this.loginChangedSubject.next();

    const user = this.getCurrentUser();

    this.mixpanelHelper.login(user?.name!, user?.company!, user?.id!);

    return data;
  }

  loginWithToken(token: string): boolean {
    tokenSetter(token);
    return this.isAuthenticated();
  }

  logout() {
    this.currenUser = null;
    localStorage.removeItem('user-change-pwd');
    localStorage.removeItem('userPermissions');
    tokenSetter(null);
    this.loginChangedSubject.next();
  }

  forgotPassword(login: string) {
    return this.http
      .get(`${this.url}/forgot-password`, {
        params: { login },
      })
      .toPromise();
  }

  async changePassword(currentPassword: string, newPassword: string) {
    await this.http
      .post<any>(`${this.url}/change-password`, {
        actual: currentPassword,
        new: newPassword,
      })
      .toPromise();

    localStorage.removeItem(CHANGE_PWD_KEY);
    this.passwordChangedSubject.next();
  }

  getUserSettings() {
    return this.http.get<IUserSettings>(`${this.url}/settings`).toPromise();
  }

  updateNotificationSettings(notificationsIds: number[]) {
    return this.http
      .post(`${this.url}/settings/notification`, notificationsIds)
      .toPromise();
  }

  requestAccess(body: IRequestAccess): any | HttpErrorResponse {
    return this.http
      .post(`${this.adminUrl}/request/access`, body)
      .pipe(catchError((err) => of(err)))
      .toPromise();
  }

  isAuthenticated() {
    try {
      return !this.jwtHelper.isTokenExpired();
    } catch {
      return false;
    }
  }

  needChangePassword() {
    if (this.isAuthenticated()) {
      return localStorage.getItem(CHANGE_PWD_KEY) !== null;
    }

    return false;
  }

  passwordChanged() {
    return this.passwordChangedSubject.asObservable();
  }

  loginChanged() {
    return this.loginChangedSubject.asObservable();
  }

  getCurrentUser() {
    if (this.isAuthenticated()) {
      this.currenUser = this.decodeToken();
      return Object.assign({}, this.currenUser);
    }

    return null;
  }

  private decodeToken() {
    const { udata } = this.jwtHelper.decodeToken();
    const { id, name, company } = JSON.parse(udata);

    return <IUser>{
      id,
      name,
      company,
      registered: id && company ? true : false,
    };
  }
}
